import { Box, Button, Field, Heading, Input, Text, VStack } from '@chakra-ui/react'
import { useState } from 'react'
import { LeadPayload } from '../pilotTypes'

interface PilotLeadCaptureProps {
  partnerName: string
  homeAddress: string
  homeId?: number
  unlocked: boolean
  formOpen: boolean
  onOpenForm: () => void
  onSubmit: (payload: LeadPayload) => Promise<void>
  isSubmitting: boolean
  apiError?: string | null
}

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/

const PilotLeadCapture: React.FC<PilotLeadCaptureProps> = ({
  partnerName,
  homeAddress,
  homeId,
  unlocked,
  formOpen,
  onOpenForm,
  onSubmit,
  isSubmitting,
  apiError,
}) => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = () => {
    const e: Record<string, string> = {}
    if (!name.trim()) e.name = 'Please enter your name'
    if (!email.trim() && !phone.trim()) {
      e.contact = 'Please enter an email address or a phone number'
    } else if (email.trim() && !EMAIL_PATTERN.test(email.trim())) {
      e.email = 'Please enter a valid email'
    }
    return e
  }

  const handleSubmit = async () => {
    const e = validate()
    if (Object.keys(e).length > 0) {
      setErrors(e)
      return
    }
    setErrors({})
    await onSubmit({
      name: name.trim(),
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      homeAddress,
      homeId,
    })
  }

  if (unlocked) {
    return (
      <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="xl" p={{ base: 4, sm: 6 }}>
        <VStack gap={2} align="stretch">
          <Heading size="md" color="green.800">✓ Price estimates unlocked</Heading>
          <Text fontSize="sm" color="green.800">
            Thanks — your personalised price estimates are now shown on this page.
            {' '}{partnerName} will be in touch for a free 20 min virtual consultation to
            answer any questions and help you navigate grants.
          </Text>
        </VStack>
      </Box>
    )
  }

  return (
    <Box bg="brand.500" borderRadius="xl" p={{ base: 4, sm: 6 }} color="white">
      <VStack gap={3} align="stretch">
        <Heading size="md" color="white">Get your free price estimates</Heading>
        <Text fontSize="sm" opacity={0.9}>
          See what these improvements are estimated to cost for your home. Enter your
          details and your personalised price estimates will appear instantly.
          {' '}{partnerName} may contact you for a free 20 min virtual consultation to
          answer any questions and help you navigate grants.
        </Text>

        {!formOpen ? (
          <Button
            bg="white"
            color="brand.500"
            _hover={{ bg: 'gray.100' }}
            size="lg"
            onClick={onOpenForm}
            fontWeight="bold"
            h="auto"
            minH="48px"
            py={3}
            whiteSpace="normal"
            lineHeight="1.3"
          >
            Get my price estimates →
          </Button>
        ) : (
          <>
            <Box bg="white" borderRadius="lg" p={{ base: 3, sm: 4 }} color="gray.800">
              <VStack gap={3} align="stretch">
                <Field.Root invalid={!!errors.name}>
                  <Field.Label>Your name</Field.Label>
                  <Input
                    placeholder="Jane Smith"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    size="lg"
                  />
                  {errors.name && <Field.ErrorText>{errors.name}</Field.ErrorText>}
                </Field.Root>

                <Field.Root invalid={!!errors.email || !!errors.contact}>
                  <Field.Label>Email address</Field.Label>
                  <Input
                    type="email"
                    placeholder="jane@example.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    size="lg"
                  />
                  {errors.email && <Field.ErrorText>{errors.email}</Field.ErrorText>}
                </Field.Root>

                <Field.Root invalid={!!errors.contact}>
                  <Field.Label>Phone number</Field.Label>
                  <Input
                    type="tel"
                    placeholder="07700 900000"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    size="lg"
                  />
                  <Field.HelperText>Enter at least one way to reach you — email or phone.</Field.HelperText>
                  {errors.contact && <Field.ErrorText>{errors.contact}</Field.ErrorText>}
                </Field.Root>
              </VStack>
            </Box>

            {apiError && (
              <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="md" p={3}>
                <Text color="red.700" fontSize="sm">{apiError}</Text>
              </Box>
            )}

            <Button
              bg="white"
              color="brand.500"
              _hover={{ bg: 'gray.100' }}
              size="lg"
              onClick={handleSubmit}
              disabled={isSubmitting}
              loading={isSubmitting}
              loadingText="Unlocking…"
              fontWeight="bold"
              h="auto"
              minH="48px"
              py={3}
              whiteSpace="normal"
              lineHeight="1.3"
            >
              Show my price estimates
            </Button>

            <Text fontSize="xs" opacity={0.8} textAlign="center">
              Your details will only be shared with {partnerName} to discuss your results and next steps.
            </Text>
          </>
        )}
      </VStack>
    </Box>
  )
}

export default PilotLeadCapture
